import { html } from "htm/preact";
import { useState } from "preact/hooks";
import { Icon } from "./Icon.js";
import { TERMS } from "../copy.js";

export function joinUrl(code: string): string {
  return `${location.origin}/#${code}`;
}

/** The link that restores organiser access on a device that has lost its storage. */
export function organiserUrl(code: string, adminToken: string): string {
  return `${location.origin}/#/t/${code}/organizer/${adminToken}`;
}

/**
 * Puts the link inside the message, rather than trusting the share sheet to keep the
 * two together.
 *
 * `navigator.share` takes `text` and `url` as separate fields, and iOS does not
 * necessarily hand both to whatever you share with. Choosing Copy puts a *URL-typed*
 * item on the clipboard whose plain-text flavour is only the message — so it pastes
 * perfectly into Notes or Messages, which read the URL flavour, and arrives in a plain
 * `<textarea>` as a sentence with no link in it. An organiser recovering access got the
 * description of their link and nothing they could use. `clipboard.readText()` sees the
 * same plain text, which is why the paste button failed identically.
 *
 * `url` is still passed as well: worst case the plain-text flavour is just this string,
 * which now contains the link, and best case the link appears twice — which the parser
 * takes in its stride, since it looks for the first one. Passing both is strictly safer
 * than either alone.
 *
 * So this is deliberate, and `text: text` is the bug. Don't tidy it back.
 */
function withLink(text: string, url: string): string {
  return text.includes(url) ? text : `${text}\n${url}`;
}

/**
 * Uses the Web Share API where it exists (iOS/Android — opens the native sheet, so
 * the link goes straight into Messages), and falls back to copying the link on
 * desktop or anywhere the API is missing or dismissed.
 */
/** Share sheet where available, clipboard everywhere else. */
export function ShareOrCopyButton({
  label,
  url,
  title,
  text,
  className = "secondary",
}: {
  label: string;
  url: string;
  title: string;
  text: string;
  className?: string;
}) {
  const [status, setStatus] = useState<string | null>(null);

  async function go() {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: withLink(text, url), url });
        return;
      } catch (err) {
        if ((err as Error)?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setStatus("Link copied");
    } catch {
      setStatus(url);
    }
    setTimeout(() => setStatus(null), 2500);
  }

  return html`
    <div class="share-wrap">
      <button class=${className} onClick=${go}><${Icon} name="link" size=${18} /> ${label}</button>
      ${status && html`<span class="share-status">${status}</span>`}
    </div>
  `;
}

export function ShareButton({ code, name }: { code: string; name: string }) {
  const [status, setStatus] = useState<string | null>(null);

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setStatus("Link copied");
    } catch {
      setStatus(url);
    }
    setTimeout(() => setStatus(null), 2500);
  }

  async function share() {
    const url = joinUrl(code);
    // Same reasoning as the organiser link — see `withLink`. It costs less here, since
    // the code is in the message and someone can always type that, but it's the same
    // defect: share to anything that keeps only the plain text and the link is gone.
    const shareData = {
      title: name || `Golf ${TERMS.event}`,
      text: withLink(`Join "${name || `our golf ${TERMS.event}`}" — code ${code}`, url),
      url,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        // AbortError just means the user closed the sheet — don't then copy behind
        // their back, or nag them about it.
        if ((err as Error)?.name === "AbortError") return;
      }
    }
    await copy(url);
  }

  return html`
    <div class="share-wrap">
      <button class="secondary" onClick=${share}>
        <${Icon} name="share" size=${18} /> Share invite link
      </button>
      ${status && html`<span class="share-status">${status}</span>`}
    </div>
  `;
}
